/* ─── ThreeScene: declarative 3D scene renderer ─── */

import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { useArtifactSendEvent } from '../store';

interface SceneObject {
  type: string;
  id?: string;
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: [number, number, number];
  color?: string;
  opacity?: number;
  wireframe?: boolean;
  // Line-specific
  points?: [number, number, number][];
  // Points-specific
  positions?: [number, number, number][];
  point_size?: number;
}

interface SceneLight {
  type: string;
  color?: string;
  intensity?: number;
  position?: [number, number, number];
}

interface SceneCamera {
  position?: [number, number, number];
  look_at?: [number, number, number];
  fov?: number;
}

function createMesh(obj: SceneObject): THREE.Object3D | null {
  const color = obj.color ?? '#7678E3';
  const opacity = obj.opacity ?? 1.0;
  const wireframe = obj.wireframe ?? false;
  const transparent = opacity < 1;

  let geometry: THREE.BufferGeometry | null = null;

  switch (obj.type) {
    case 'box':
      geometry = new THREE.BoxGeometry(1, 1, 1);
      break;
    case 'sphere':
      geometry = new THREE.SphereGeometry(1, 32, 16);
      break;
    case 'cylinder':
      geometry = new THREE.CylinderGeometry(1, 1, 1, 32);
      break;
    case 'plane':
      geometry = new THREE.PlaneGeometry(1, 1);
      break;
    case 'line': {
      if (!obj.points || obj.points.length < 2) return null;
      const lineGeo = new THREE.BufferGeometry();
      const verts = new Float32Array(obj.points.flat());
      lineGeo.setAttribute('position', new THREE.BufferAttribute(verts, 3));
      const lineMat = new THREE.LineBasicMaterial({ color });
      const line = new THREE.Line(lineGeo, lineMat);
      if (obj.id) line.userData.objectId = obj.id;
      return line;
    }
    case 'points': {
      if (!obj.positions || obj.positions.length === 0) return null;
      const ptGeo = new THREE.BufferGeometry();
      const ptVerts = new Float32Array(obj.positions.flat());
      ptGeo.setAttribute('position', new THREE.BufferAttribute(ptVerts, 3));
      const ptMat = new THREE.PointsMaterial({
        color,
        size: obj.point_size ?? 0.05,
        sizeAttenuation: true,
      });
      const pts = new THREE.Points(ptGeo, ptMat);
      if (obj.id) pts.userData.objectId = obj.id;
      return pts;
    }
    default:
      return null;
  }

  if (!geometry) return null;

  const material = new THREE.MeshStandardMaterial({
    color,
    opacity,
    transparent,
    wireframe,
    side: obj.type === 'plane' ? THREE.DoubleSide : THREE.FrontSide,
  });
  const mesh = new THREE.Mesh(geometry, material);

  if (obj.position) mesh.position.set(...obj.position);
  if (obj.rotation) mesh.rotation.set(...obj.rotation);
  if (obj.scale) mesh.scale.set(...obj.scale);
  if (obj.id) mesh.userData.objectId = obj.id;

  return mesh;
}

function createLight(light: SceneLight): THREE.Light | null {
  const color = light.color ?? '#ffffff';
  const intensity = light.intensity ?? 1;

  switch (light.type) {
    case 'ambient':
      return new THREE.AmbientLight(color, intensity);
    case 'directional': {
      const dl = new THREE.DirectionalLight(color, intensity);
      if (light.position) dl.position.set(...light.position);
      return dl;
    }
    case 'point': {
      const pl = new THREE.PointLight(color, intensity);
      if (light.position) pl.position.set(...light.position);
      return pl;
    }
    default:
      return null;
  }
}

// Pseudo-values mapped to "no scene background" — the WebGL canvas
// is created with `alpha: true`, so leaving `scene.background` unset
// lets the artifact panel surface show through. That keeps the scene
// theme-portable: Ink on dark, Paper on light, controlled by the
// surrounding panel rather than baked into the figure.
function isTransparentBackground(value: string | null | undefined): boolean {
  if (value == null) return true;
  const v = value.trim().toLowerCase();
  return v === '' || v === 'transparent' || v === 'none';
}

export function ArtifactThreeScene({
  objects = [],
  lights = [],
  camera: cameraCfg,
  background = 'transparent',
  controls: enableControls = true,
  __node_id,
}: {
  objects?: SceneObject[];
  lights?: SceneLight[];
  camera?: SceneCamera;
  background?: string;
  controls?: boolean;
  __node_id?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef<{
    renderer: THREE.WebGLRenderer;
    scene: THREE.Scene;
    camera: THREE.PerspectiveCamera;
    controls: OrbitControls | null;
    animId: number;
    clickables: THREE.Object3D[];
  } | null>(null);
  const sendEvent = useArtifactSendEvent();

  // Initialize renderer once
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const width = container.clientWidth;
    const height = Math.max(400, container.clientHeight);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    container.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const fov = cameraCfg?.fov ?? 50;
    const cam = new THREE.PerspectiveCamera(fov, width / height, 0.1, 1000);
    const pos = cameraCfg?.position ?? [5, 5, 5] as [number, number, number];
    cam.position.set(...pos);

    let ctl: OrbitControls | null = null;
    if (enableControls) {
      ctl = new OrbitControls(cam, renderer.domElement);
      ctl.enableDamping = true;
      ctl.dampingFactor = 0.05;
    }
    const lookAt = cameraCfg?.look_at ?? [0, 0, 0] as [number, number, number];
    cam.lookAt(...lookAt);
    if (ctl) {
      ctl.target.set(...lookAt);
      ctl.update();
    }

    // Raycaster for click detection
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    const onClick = (e: MouseEvent) => {
      if (!__node_id) return;
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, cam);
      const clickables = stateRef.current?.clickables ?? [];
      const intersects = raycaster.intersectObjects(clickables, false);
      if (intersects.length > 0) {
        const hit = intersects[0];
        const objectId = hit.object.userData.objectId ?? '';
        const pt = hit.point;
        sendEvent(__node_id, 'three_click', {
          kind: 'ThreeClicked',
          object_id: objectId,
          point: [pt.x, pt.y, pt.z],
        });
      }
    };
    renderer.domElement.addEventListener('click', onClick);

    let animId = 0;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      ctl?.update();
      renderer.render(scene, cam);
    };
    animate();

    stateRef.current = { renderer, scene, camera: cam, controls: ctl, animId, clickables: [] };

    // Resize observer
    const resizeObs = new ResizeObserver(() => {
      const w = container.clientWidth;
      const h = Math.max(400, container.clientHeight);
      renderer.setSize(w, h);
      cam.aspect = w / h;
      cam.updateProjectionMatrix();
    });
    resizeObs.observe(container);

    return () => {
      cancelAnimationFrame(animId);
      renderer.domElement.removeEventListener('click', onClick);
      resizeObs.disconnect();
      ctl?.dispose();
      renderer.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      stateRef.current = null;
    };
  }, [enableControls]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update scene contents when props change
  useEffect(() => {
    const state = stateRef.current;
    if (!state) return;

    const { scene } = state;

    // Clear scene
    while (scene.children.length > 0) {
      scene.remove(scene.children[0]);
    }

    scene.background = isTransparentBackground(background) ? null : new THREE.Color(background);

    // Lights
    for (const lightCfg of lights) {
      const light = createLight(lightCfg);
      if (light) scene.add(light);
    }

    // Objects
    const clickables: THREE.Object3D[] = [];
    for (const objCfg of objects) {
      const obj3d = createMesh(objCfg);
      if (obj3d) {
        scene.add(obj3d);
        if (objCfg.id) clickables.push(obj3d);
      }
    }
    state.clickables = clickables;
  }, [objects, lights, background]);

  return (
    <div
      ref={containerRef}
      className="a-three-scene"
      style={{
        width: '100%',
        minHeight: 400,
        borderRadius: 'var(--artifact-border-radius)',
        overflow: 'hidden',
        border: '1px solid var(--artifact-border)',
      }}
    />
  );
}
